import { useEffect, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, DownOutlined, TrophyOutlined, SearchOutlined, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { getElasticSearchService, getPipelinesService } from "../backend";
import { Avatar, Button, Card, Col, Dropdown, Menu, notification, Row, Skeleton, Tag } from "antd";
import { Pipeline, PipelineBuildDetails } from "@microtica/ms-ap-sdk";
import GitCommitLink from "../components/GitCommitLink";
import moment from "moment";
import { PipelineMetricsResponseResponse } from "@microtica/ms-elasticsearch-sdk";
import DisplayMetric from "./DisplayMetric";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Search from "antd/lib/input/Search";
import Title from "antd/lib/typography/Title";
import InfiniteScroll from "react-infinite-scroll-component";
import ExplanationButton from "./explanations/ExplanationButton";
import PipelineBuildsExplanation from "./explanations/PipelineBuildsExplanation";

interface PipelineBuildsProps {
    buildPath: string;
    pipelineId: string;
    projectId: string;
}
const PipelineBuilds = ({
    projectId,
    buildPath,
    pipelineId
}: PipelineBuildsProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [builds, setBuilds] = useState<PipelineBuildDetails[]>([]);
    const [pipelineMetrics, setPipelineMetrics] = useState<PipelineMetricsResponseResponse>();
    const [loadingPipelineMetrics, setLoadingPipelineMetrics] = useState(true);
    const [loadingPipelineBuilds, setLoadingPipelineBuilds] = useState(true);
    const [filteredBuilds, setFilteredBuilds] = useState(builds);   // [] initially
    const [paginationConfig, setPaginationConfig] = useState<{ limit: number, offset: number, hasMore: boolean }>({ limit: 10, offset: 0, hasMore: true });
    const [repositoryBranches, setRepositoryBranches] = useState<string[]>([]);
    const [pipelineDetails, setPipelineDetails] = useState<Pipeline>();

    useEffect(() => {
        if (pipelineId) {
            loadData();
        }
    }, [pipelineId])

    useEffect(() => {
        if (pipelineDetails) {
            getRepositoryBranches();
        }
    }, [pipelineDetails])

    const loadData = async () => {
        await Promise.all([
            getPipelineMetrics(),
            getPipelineBuilds(),
            getPipelineDetails()
        ]);
    }

    const getPipelineMetrics = async () => {
        setLoadingPipelineMetrics(true);
        const { data: { response } } = await getElasticSearchService().getPipelineMetrics(projectId, pipelineId);
        setPipelineMetrics(response);
        setLoadingPipelineMetrics(false);
    }

    const getPipelineBuilds = async () => {
        const { builds } = await fetchPipelineBuilds({
            offset: paginationConfig.offset,
            limit: paginationConfig.limit
        });
        setBuilds(prevState => ([...prevState, ...builds]));
        setFilteredBuilds(prevState => ([...prevState, ...builds]));

        setPaginationConfig(prevState => ({
            limit: prevState.limit,
            offset: prevState.offset + prevState.limit,
            hasMore: !!builds.length ? true : false
        }));
    }

    const fetchPipelineBuilds = async ({ offset, limit }: { offset: number, limit: number }): Promise<{ builds: PipelineBuildDetails[] }> => {
        setLoadingPipelineBuilds(true);
        const { data: { builds } } = await getPipelinesService().getPipelineBuilds(
            projectId,
            pipelineId,
            offset,
            limit
        ).catch(_ => {
            return { data: { builds: [] } };
        });
        setLoadingPipelineBuilds(false);
        return { builds };
    }

    const renderStatusColor = (status: string) => {
        switch (status) {
            case "SUCCEEDED":
                return "green"
            case "FAILED":
                return "red";
            case "RUNNING":
                return "blue";
        }
    }

    const handleSearch = (searchQuery: string) => {
        setFilteredBuilds(builds.filter(build => build.id.includes(searchQuery)));
    }

    const getRepositoryBranches = async () => {
        const { data: { branches } } = await getPipelinesService()
            .listGitAccountRepositoryBranches(projectId, pipelineDetails?.gitAccountId!, pipelineDetails?.repositoryUrl!);
        setRepositoryBranches(branches);
    }

    const getPipelineDetails = async () => {
        const { data: pipeline } = await getPipelinesService().getPipelineById(projectId, pipelineId);
        setPipelineDetails(pipeline);
    }

    const handlePipelineTrigger = async (branch: string) => {
        try {
            const { data: { buildId } } = await getPipelinesService()
                .triggerPipeline(projectId, pipelineId, { ref: `refs/heads/${branch}` });
            notification.success({
                message: "Pipeline build triggered",
                description: "It could take a few moments for the changes to take effect."
            });
            setTimeout(() => {
                navigate(`${location.pathname}/${pipelineId}/build/${buildId}`);
            }, 2000);
        } catch (error) {
            notification.error({
                message: "Error triggering build",
                description: (error as any).response?.data?.message || "We encountered an error while trying to trigger a build. Please try again."
            });
        }
    }

    const handleRefreshBuilds = async () => {
        const { builds } = await fetchPipelineBuilds({
            offset: 0,
            limit: 10
        });
        setBuilds(builds);
        setFilteredBuilds(builds);
        setPaginationConfig({
            offset: 10,
            limit: 10,
            hasMore: !!builds.length ? true : false
        });
    }

    return (
        <Card title={
            <>
                <span>Pipeline builds</span>
                <ExplanationButton
                    content={<PipelineBuildsExplanation />}
                />
                <span style={{ float: "right" }}>
                    <Button
                        type="ghost"
                        onClick={handleRefreshBuilds}
                        style={{ marginRight: 10 }}
                    >
                        <ReloadOutlined /> Refresh builds
                    </Button>

                    <Dropdown
                        trigger={["click"]}
                        placement="bottomRight"
                        overlay={
                            <Menu direction="ltr">
                                <Menu.Item
                                    key="choose"
                                    style={{ pointerEvents: "none" }}
                                >
                                    Choose branch:
                                </Menu.Item>
                                <Menu.Divider />
                                {
                                    repositoryBranches.map(branch => (
                                        <Menu.Item
                                            key={branch}
                                            onClick={() => handlePipelineTrigger(branch)}
                                        >
                                            {branch}
                                        </Menu.Item>
                                    ))
                                }
                            </Menu>

                        }>
                        <Button type="primary">
                            Trigger pipeline <DownOutlined />
                        </Button>
                    </Dropdown>
                </span>
            </>
        }>
            {
                loadingPipelineMetrics ?
                    <Skeleton /> :
                    <Row gutter={24}>
                        <Col xs={24} sm={12} md={6}>
                            <DisplayMetric
                                size="medium"
                                icon={<CheckCircleOutlined />}
                                title="Successful builds"
                                data={pipelineMetrics?.successfulExecutions!}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <DisplayMetric
                                size="medium"
                                icon={<CloseCircleOutlined />}
                                title="Failed builds"
                                data={pipelineMetrics?.failedExecutions!}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <DisplayMetric
                                size="medium"
                                icon={<TrophyOutlined />}
                                title="Success rate"
                                data={`${(pipelineMetrics?.successfulExecutions! / (pipelineMetrics?.successfulExecutions! + pipelineMetrics?.failedExecutions!) * 100).toFixed(0)}%`}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <DisplayMetric
                                size="medium"
                                icon={<ClockCircleOutlined />}
                                title="Total build time"
                                data={
                                    pipelineMetrics?.buildTime ?
                                        moment.utc(
                                            pipelineMetrics?.buildTime
                                        ).format("m[min] s[sec]")
                                            .replace(/^0min /, "")
                                            .replace(/^0sec$/, "")
                                        : "0min"
                                }
                            />
                        </Col>
                    </Row>
            }

            {/* Search box */}
            <Search
                style={{ margin: "25px 0" }}
                size="large"
                placeholder="Search build executions..."
                onKeyUp={(event: any) => handleSearch(event.target.value)}
                onSearch={handleSearch}
            />

            {
                loadingPipelineBuilds ?
                    <Skeleton /> :
                    <Card
                        type="inner"
                        title={
                            <Row gutter={24}>
                                <Col xs={24} sm={12} md={4}>
                                    Status
                                </Col>
                                <Col xs={24} sm={12} md={9}>
                                    Execution
                                </Col>
                                <Col xs={24} sm={12} md={8}>
                                    Commit
                                </Col>
                                <Col xs={24} sm={12} md={3} style={{ textAlign: "right" }}>
                                    Time
                                </Col>
                            </Row>
                        }>
                        <div
                            id="scrollableDiv"
                            style={{
                                height: 500,
                                overflow: "auto"
                            }}
                        >
                            <InfiniteScroll
                                scrollableTarget="scrollableDiv"
                                dataLength={filteredBuilds.length}
                                next={getPipelineBuilds}
                                hasMore={paginationConfig.hasMore && !!filteredBuilds.length}
                                loader={
                                    <div className="infinite-scroll-loading">
                                        <LoadingOutlined />
                                    </div>
                                }
                                style={{ padding: "0 10px" }}
                            >
                                {
                                    filteredBuilds.length ?
                                        filteredBuilds.map(build => (
                                            <Row gutter={24} key={build.id} className="pipelines-item" align="middle">
                                                <Col xs={24} sm={12} md={4}>
                                                    <Tag color={renderStatusColor(build.status)}>
                                                        {build.status}
                                                    </Tag>
                                                </Col>
                                                <Col xs={24} sm={12} md={9}>
                                                    <Link to={`${buildPath}/pipeline/${pipelineId}/build/${build.id}`}>
                                                        {build.id}
                                                    </Link>
                                                </Col>
                                                <Col xs={24} sm={12} md={8}>
                                                    {
                                                        build.metadata.commit ?
                                                            <div style={{ fontSize: "12px" }}>
                                                                <b>{build.metadata.commit.message}</b>
                                                                <div>
                                                                    Commit by <Avatar src={build.metadata.commit.user.avatar} size={16} /> {build.metadata.commit.user.name}
                                                                </div>
                                                                <div>
                                                                    <GitCommitLink version={build.metadata.commit.sha || build.metadata.commit.version} repoUrl={build.metadata.repository} branch={build.metadata.commit.name} />
                                                                </div>
                                                            </div> :
                                                            <>No deployment information available</>
                                                    }
                                                </Col>
                                                <Col xs={24} sm={12} md={3} style={{ textAlign: "right" }}>
                                                    <div style={{ fontWeight: 500 }}>
                                                        {
                                                            moment.utc(
                                                                moment.duration(moment(build.stopDate || new Date()).diff(moment(build.startDate))).as("milliseconds")
                                                            ).format("m[min] s[sec]")
                                                                .replace(/^0min /, "")
                                                                .replace(/^0sec$/, "")
                                                        }
                                                    </div>
                                                    <div style={{ color: "gray" }}>
                                                        {build.startDate ? moment(build.startDate).fromNow() : ""}
                                                    </div>
                                                </Col>
                                            </Row>
                                        )) :
                                        <div style={{ textAlign: "center" }}>
                                            <div style={{ textAlign: 'center' }}>
                                                <SearchOutlined style={{ fontSize: 60, verticalAlign: "middle", color: "#244460" }} />
                                                <Title level={5} style={{ color: "black" }}>It looks like there are no results with that query</Title>
                                                <p style={{ color: "black" }}>Please try another search</p>
                                            </div>
                                        </div>
                                }
                            </InfiniteScroll>
                        </div>
                    </Card>
            }
        </Card >
    )
}

export default PipelineBuilds;